// Dismissible "How to read this" explainer card (design §5.8) — sits above a
// visualisation, gives a one-line orientation plus a worked example, and
// stays dismissed per-visualisation, per-browser once closed. Concepts live
// inside an SVG/canvas that a ? popover can't anchor to belong here instead.

import { useState } from 'react'
import { X, Lightbulb } from 'lucide-react'

interface HowToReadThisCardProps {
  /** stable id — namespaces the dismissed flag, e.g. "portfolio-atlas" */
  visualisationId: string
  title: string
  /** one or two sentences on what the picture shows */
  body: string
  /** a concrete worked example grounded in what's actually on screen */
  example: string
}

const DISMISSED_PREFIX = 'howToReadDismissed:'

export default function HowToReadThisCard({ visualisationId, title, body, example }: HowToReadThisCardProps) {
  const storageKey = `${DISMISSED_PREFIX}${visualisationId}`
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === '1'
    } catch {
      return false
    }
  })

  if (dismissed) return null

  const dismiss = () => {
    setDismissed(true)
    try {
      localStorage.setItem(storageKey, '1')
    } catch {
      /* non-fatal */
    }
  }

  return (
    <div
      data-testid={`how-to-read-${visualisationId}`}
      className="mb-3 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5"
    >
      <Lightbulb size={16} className="mt-0.5 flex-shrink-0 text-amber-500" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-semibold text-amber-900">How to read this: {title}</p>
        <p className="mt-0.5 text-[11.5px] leading-relaxed text-amber-800">{body}</p>
        <p className="mt-1 text-[11.5px] italic leading-relaxed text-amber-700">{example}</p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="flex-shrink-0 text-amber-500 hover:text-amber-700"
      >
        <X size={14} />
      </button>
    </div>
  )
}
